import { useState, useEffect } from "react";
import { fetchEnergyJson } from "../../../utils/energyCache";

/** Shared fetch hook — one API call, typed response */
export function useEnergyTypeFetch<T>(url:string, enabled:boolean) {
  const [data,    setData]    = useState<T|null>(null);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState<string|null>(null);
  useEffect(()=>{
    if(!enabled||!url){ if(!enabled) setError("siteId ou URL manquant."); return; }
    let cancelled=false;
    setLoading(true); setError(null);
    fetchEnergyJson(url)
      .then((d)=>{ if(!cancelled){ setData(d as T); setLoading(false); } })
      .catch((e:unknown)=>{ if(!cancelled){ setError((e as Error).message); setLoading(false); } });
    return()=>{ cancelled=true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  },[url, enabled]);
  return { data, loading, error };
}

/** Shared visual shell for energy blocks */
export function EnergyBlockShell({title,meterLabel,loading,error,chartRef,height}:{
  title:string; meterLabel?:string; loading:boolean; error:string|null;
  chartRef:{current:HTMLDivElement|null}; height:number;
}) {
  return(
    <div>
      <div style={{fontSize:8,fontWeight:300,letterSpacing:3,textTransform:"uppercase",color:"#aaa",marginBottom:meterLabel?2:6}}>
        {title}
      </div>
      {meterLabel && (
        <div style={{fontSize:9,color:"#888",marginBottom:4,fontStyle:"italic",letterSpacing:0.3}}>
          {meterLabel}
        </div>
      )}
      {loading && (
        <div style={{height,display:"flex",alignItems:"center",justifyContent:"center",color:"#aaa",fontSize:12,gap:8}}>
          <span style={{animation:"spin 1s linear infinite",display:"inline-block"}}>⟳</span> Chargement…
        </div>
      )}
      {error && (
        <div style={{height,display:"flex",alignItems:"center",justifyContent:"center",color:"var(--red)",fontSize:11,padding:"0 20px",textAlign:"center"}}>
          ⚠ {error}
        </div>
      )}
      {!loading && !error && <div ref={chartRef} style={{height,width:"100%",position:"relative",zIndex:1}}/>}
    </div>
  );
}

/** Load HC + optional module, then call render fn */
export async function loadHCAndRender(render:()=>void, needHeatmap=false, needVariwide=false) {
  if(!window.Highcharts){
    await new Promise<void>(res=>{
      const sc=document.createElement("script");
      sc.src="https://cdnjs.cloudflare.com/ajax/libs/highcharts/11.2.0/highcharts.js";
      sc.onload=()=>res(); document.head.appendChild(sc);
    });
  }
  if(needHeatmap && !(window.Highcharts as any).seriesTypes?.heatmap){
    await new Promise<void>(res=>{
      const sc=document.createElement("script");
      sc.src="https://cdnjs.cloudflare.com/ajax/libs/highcharts/11.2.0/modules/heatmap.js";
      sc.onload=()=>res(); document.head.appendChild(sc);
    });
  }
  if(needVariwide && !(window.Highcharts as any).seriesTypes?.variwide){
    await new Promise<void>(res=>{
      const sc=document.createElement("script");
      sc.src="https://cdnjs.cloudflare.com/ajax/libs/highcharts/11.2.0/modules/variwide.js";
      sc.onload=()=>res(); document.head.appendChild(sc);
    });
  }
  render();
}

/** Format ISO date (YYYY-MM-DD) as DD/MM/YY for compact tooltip display */
export const fmtRelDate = (iso:string):string => {
  const [y,m,d]=iso.split("-");
  return `${d}/${m}/${y.slice(2)}`;
};

/** Interpolate hex colour between two values (0=from, 1=to) */
export function lerpHex(from:string, to:string, t:number):string {
  const h=(s:string)=>[parseInt(s.slice(1,3),16),parseInt(s.slice(3,5),16),parseInt(s.slice(5,7),16)];
  const [r1,g1,b1]=h(from); const [r2,g2,b2]=h(to);
  const r=Math.round(r1+(r2-r1)*t); const g=Math.round(g1+(g2-g1)*t); const b=Math.round(b1+(b2-b1)*t);
  return `#${r.toString(16).padStart(2,"0")}${g.toString(16).padStart(2,"0")}${b.toString(16).padStart(2,"0")}`;
}
